import { useEffect, useState } from 'react';
import { Link } from '../lib/routing';
import { HeaderContent, HEADER_TITLE_CLASS, HEADER_BADGE_CLASS } from '../components/shell/app-header';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { NativeSelect } from '@/components/ui/select';
import { Alert } from '@/components/ui/alert';
import { Key } from '@phosphor-icons/react';

const HEADER_ICON = <Key size={26} />;
import {
  assignRole, inviteUser, fetchAdminSchools,
  type SchoolRow,
} from '../lib/admin-users';

const ROLES = ['admin', 'direction', 'tutor'] as const;
type RoleOption = (typeof ROLES)[number];

const ROLE_LABELS: Record<RoleOption, string> = {
  admin: 'Admin plateforme',
  direction: 'Direction',
  tutor: 'Parent / tuteur',
};


function roleScopeType(role: RoleOption): 'platform' | 'school' | 'user' {
  if (role === 'admin') return 'platform';
  if (role === 'tutor') return 'user';
  return 'school';
}

export default function AdminUsersAcces() {
  const [schools, setSchools] = useState<SchoolRow[]>([]);
  const [busy, setBusy] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const [assignEmail, setAssignEmail] = useState('');
  const [assignRole_, setAssignRole] = useState<RoleOption>('direction');
  const [assignSchoolId, setAssignSchoolId] = useState('');

  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole_, setInviteRole] = useState<RoleOption>('direction');
  const [inviteSchoolId, setInviteSchoolId] = useState('');

  useEffect(() => {
    fetchAdminSchools().then(setSchools).catch(() => setLoadFailed(true));
  }, []);

  const flash = (msg: string) => { setNotice(msg); setTimeout(() => setNotice(null), 5000); };

  const onAssignRole = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!assignEmail.trim()) return;
    const scopeType = roleScopeType(assignRole_);
    const scopeId = scopeType === 'school' ? assignSchoolId || undefined : undefined;
    if (scopeType === 'school' && !scopeId) { flash('Sélectionne un établissement pour ce rôle.'); return; }
    setBusy(true);
    try {
      const res = await assignRole(assignEmail.trim(), assignRole_, scopeType, scopeId);
      flash(res.status === 'already' ? 'Rôle déjà présent.' : `Rôle « ${ROLE_LABELS[assignRole_]} » assigné à ${assignEmail.trim()}.`);
      setAssignEmail('');
    } catch (e) {
      flash(e instanceof Error ? e.message : String(e));
    } finally { setBusy(false); }
  };

  const onInviteUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return;
    const scopeType = roleScopeType(inviteRole_);
    const scopeId = scopeType === 'school' ? inviteSchoolId || undefined : undefined;
    if (scopeType === 'school' && !scopeId) { flash('Sélectionne un établissement pour ce rôle.'); return; }
    setBusy(true);
    try {
      const res = await inviteUser(inviteEmail.trim(), inviteRole_, scopeType, scopeId);
      flash(
        res.status === 'assigned'
          ? `${inviteEmail.trim()} a déjà un compte — rôle assigné directement.`
          : `Invitation envoyée à ${inviteEmail.trim()}.`,
      );
      setInviteEmail('');
    } catch (e) {
      flash(e instanceof Error ? e.message : String(e));
    } finally { setBusy(false); }
  };

  const needsSchool = (role: RoleOption) => roleScopeType(role) === 'school';

  return (
    <>
      <HeaderContent width="md" showBurger backTo="/admin/users" backLabel="Utilisateurs & Rôles" icon={HEADER_ICON}>
        <div className="min-w-0">
          <div className="flex items-center gap-2.5 min-w-0">
            <h1 className={HEADER_TITLE_CLASS}>Accès & Rôles</h1>
            <span className={HEADER_BADGE_CLASS}>Admin</span>
          </div>
          <p className="m-0 mt-0.5 text-[13px] sm:text-[13.5px] leading-snug text-muted-foreground">
            Assigner un rôle à un compte existant ou inviter un nouvel utilisateur.
          </p>
        </div>
      </HeaderContent>
      <main className="max-w-[880px] mx-auto px-4 pb-16 pt-4">

        {loadFailed && (
          <Alert variant="destructive" className="mb-4">
            La liste des établissements n'a pas pu être chargée. Les formulaires ci-dessous restent utilisables si vous connaissez l'établissement visé.
          </Alert>
        )}

        {notice && (
          <p className="mb-4 px-4 py-3 rounded-[10px] bg-naia-sky text-naia-blue text-sm font-semibold">{notice}</p>
        )}

        <div className="flex flex-col gap-4">
          <section className="bg-[var(--naia-card)] border border-[var(--naia-line)] rounded-[var(--naia-r-card)] shadow-[var(--naia-shadow-card)] px-5 py-4">
            <h2 className="text-[11px] font-black tracking-[1.2px] uppercase mb-4 mt-0 text-[var(--naia-text-secondary)]">
              Assigner un rôle (compte existant)
            </h2>
            <form onSubmit={onAssignRole} className="flex flex-col gap-3 max-w-[480px]">
              <label className="flex flex-col gap-1.5 text-[12px] font-bold text-[var(--naia-text-primary)]">
                Email
                <Input type="email" value={assignEmail} onChange={(e) => setAssignEmail(e.target.value)} placeholder="user@exemple.fr" required />
              </label>
              <label className="flex flex-col gap-1.5 text-[12px] font-bold text-[var(--naia-text-primary)]">
                Rôle
                <NativeSelect className="bg-secondary" value={assignRole_} onChange={(e) => setAssignRole(e.target.value as RoleOption)}>
                  {ROLES.map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
                </NativeSelect>
              </label>
              {needsSchool(assignRole_) && (
                <label className="flex flex-col gap-1.5 text-[12px] font-bold text-[var(--naia-text-primary)]">
                  Établissement
                  <NativeSelect className="bg-secondary" value={assignSchoolId} onChange={(e) => setAssignSchoolId(e.target.value)} required>
                    <option value="">— choisir —</option>
                    {schools.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </NativeSelect>
                </label>
              )}
              <Button type="submit" disabled={busy} className="self-start">{busy ? '…' : 'Assigner'}</Button>
            </form>
            <p className="text-[11.5px] text-[var(--naia-text-secondary)] mt-3 mb-0">
              Pour les enseignants, passe par{' '}
              <Link to="/direction/enseignants" className="text-naia-blue font-bold">Enseignants → espace direction</Link>
              {' '}— seul chemin qui crée la fiche prof (nom, classes).
            </p>
          </section>

          <section className="bg-[var(--naia-card)] border border-[var(--naia-line)] rounded-[var(--naia-r-card)] shadow-[var(--naia-shadow-card)] px-5 py-4">
            <h2 className="text-[11px] font-black tracking-[1.2px] uppercase mb-4 mt-0 text-[var(--naia-text-secondary)]">
              Inviter un utilisateur (email + rôle)
            </h2>
            <p className="text-[12.5px] text-[var(--naia-text-secondary)] mb-3 mt-0 leading-relaxed">
              Si l'email n'a pas encore de compte, un lien de création sera envoyé. Si un compte existe déjà, le rôle est assigné directement.
            </p>
            <form onSubmit={onInviteUser} className="flex flex-col gap-3 max-w-[480px]">
              <label className="flex flex-col gap-1.5 text-[12px] font-bold text-[var(--naia-text-primary)]">
                Email à inviter
                <Input type="email" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} placeholder="nouveau@exemple.fr" required />
              </label>
              <label className="flex flex-col gap-1.5 text-[12px] font-bold text-[var(--naia-text-primary)]">
                Rôle
                <NativeSelect className="bg-secondary" value={inviteRole_} onChange={(e) => setInviteRole(e.target.value as RoleOption)}>
                  {ROLES.map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
                </NativeSelect>
              </label>
              {needsSchool(inviteRole_) && (
                <label className="flex flex-col gap-1.5 text-[12px] font-bold text-[var(--naia-text-primary)]">
                  Établissement
                  <NativeSelect className="bg-secondary" value={inviteSchoolId} onChange={(e) => setInviteSchoolId(e.target.value)} required>
                    <option value="">— choisir —</option>
                    {schools.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </NativeSelect>
                </label>
              )}
              <Button type="submit" disabled={busy} className="self-start">{busy ? 'Envoi…' : 'Inviter'}</Button>
            </form>
          </section>
        </div>
      </main>
    </>
  );
}
